/**
 * ReckhowPRetentionPage — Reckhow 1979 in-lake [TP] from areal P loading
 *
 * [P]_lake = L_P / (q_s + 11.6 + 1.2·q_s) — refined US-lake calibration of
 * Vollenweider 1976. Cross-checks the Vollenweider prediction used elsewhere.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Leaf, ArrowLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

export default function ReckhowPRetentionPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('reckhow_p_retention');
  const [L_P, setL_P] = useState(1.2);            // areal P loading g/m²/yr
  const [q_s, setQ_s] = useState(15);              // hydraulic loading rate m/yr
  const [v_s, setV_s] = useState(12);              // Reckhow & Chapra apparent settling velocity m/yr
  const [meanDepth, setDepth] = useState(3.0);

  const R = useMemo(() => {
    // Vollenweider 1976: [P] = L / (v_s + q_s)
    const P_vollenweider = L_P / Math.max(0.1, v_s + q_s);
    // Reckhow 1979: [P] = L / (11.6 + 1.2·q_s)
    const P_reckhow = L_P / Math.max(0.1, 11.6 + 1.2 * q_s);
    // Dillon & Rigler 1974: [P] = L·(1-R)/(z·rho) with R from regression
    const HRT = meanDepth / Math.max(0.1, q_s);     // years
    const R_DR = 1 / (1 + Math.sqrt(HRT));            // retention coefficient
    const P_dillon = L_P * (1 - R_DR) / Math.max(0.1, meanDepth);
    // Classify trophic state by Reckhow result (mg/L → µg/L)
    const P_ug = P_reckhow * 1000;
    const trophic = P_ug < 10 ? 'oligotrophic'
                   : P_ug < 35 ? 'mesotrophic'
                   : P_ug < 100 ? 'eutrophic' : 'hypereutrophic';
    return { P_vollenweider, P_reckhow, P_dillon, HRT, R_DR, trophic };
  }, [L_P, q_s, v_s, meanDepth]);

  const fmt = (n: number, d = 2) => isFinite(n) ? n.toFixed(d) : '—';
  const trophicColour = (t: string) => t === 'oligotrophic' ? 'text-emerald-300' : t === 'mesotrophic' ? 'text-sky-300' : t === 'eutrophic' ? 'text-amber-300' : 'text-red-300';

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={Leaf} title="Module — Reckhow P-Retention" subtitle="Cross-check on Vollenweider in-lake [TP] prediction"
        actions={<div className="flex items-center gap-2 flex-wrap">
          <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'}`}>
            <input type="checkbox" checked={enabled} onChange={() => toggle('reckhow_p_retention')} className="accent-emerald-500" /> Module {enabled ? 'ENABLED' : 'DISABLED'}
          </label>
          <button onClick={() => navigate('/modules')} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700"><ArrowLeft className="w-4 h-4" /> Modules</button>
        </div>}
      />
      <ModuleConflictBanner moduleId="reckhow_p_retention" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Vollenweider 1976</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(R.P_vollenweider * 1000, 1)} µg/L</div>
          <div className="text-xs text-slate-500 mt-1">L / (v_s + q_s)</div>
        </div>
        <div className="rounded-xl border-2 border-violet-700 bg-violet-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-violet-300">Reckhow 1979</div>
          <div className="text-2xl font-bold text-violet-200 mt-1">{fmt(R.P_reckhow * 1000, 1)} µg/L</div>
          <div className={`text-xs mt-1 capitalize ${trophicColour(R.trophic)}`}>{R.trophic}</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Dillon & Rigler 1974</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(R.P_dillon * 1000, 1)} µg/L</div>
          <div className="text-xs text-slate-500 mt-1">R = {fmt(R.R_DR, 2)} · HRT {fmt(R.HRT, 2)} yr</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
        {[
          { l: 'Areal P loading L_P (g/m²/yr)', v: L_P, set: setL_P, min: 0, max: 5, step: 0.05 },
          { l: 'Hydraulic loading q_s (m/yr)',   v: q_s, set: setQ_s, min: 1, max: 50, step: 1 },
          { l: 'Settling velocity v_s (m/yr)',   v: v_s, set: setV_s, min: 2, max: 30, step: 0.5 },
          { l: 'Mean depth (m)',                 v: meanDepth, set: setDepth, min: 0.5, max: 30, step: 0.1 },
        ].map(f => (
          <div key={f.l}>
            <label className="text-slate-400 block mb-1">{f.l}: <strong className="text-slate-100">{fmt(f.v, 2)}</strong></label>
            <input type="range" min={f.min} max={f.max} step={f.step} value={f.v}
              onChange={e => f.set(Number(e.target.value))} className="w-full accent-sky-500" />
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2"><Info className="w-4 h-4 text-rb-brand" /> Why three models</div>
        <p>The same physical input — areal P loading from the catchment — can be turned into a predicted in-lake
        TP concentration by three different empirical models. They generally agree within ±30% for typical
        UK lowland reservoirs but diverge for very shallow or very deep systems:</p>
        <ul className="list-disc ml-5 space-y-0.5 mt-1">
          <li><strong className="text-slate-200">Vollenweider 1976</strong> — original framework, simple mass balance.</li>
          <li><strong className="text-violet-300">Reckhow 1979</strong> — US-lake calibration including 47 ponds. Tends to predict slightly higher TP than Vollenweider for fast-flushing systems.</li>
          <li><strong className="text-slate-200">Dillon & Rigler 1974</strong> — adds depth dependence explicitly. Best for shallow reservoirs &lt; 5 m.</li>
        </ul>
        <p className="mt-2">When this module is enabled, the Algal Dynamics page cross-references all three TP
        estimates and flags inconsistencies. Three-way agreement gives confidence; three-way disagreement
        means the catchment loading estimate (L_P) itself needs validating before any downstream conclusions.</p>
        <p className="italic text-slate-500 mt-2">References: Vollenweider 1976; Reckhow 1979; Dillon & Rigler 1974.</p>
      </div>
    </div>
  );
}
